import axios from "axios";
import {
  buildAdminWalkInCreateBody,
  pickWalkInTrackingToken,
} from "../utils/adminWalkInBooking";

function getApiBase() {
  return (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "") + "/";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

function pick(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  if (data && typeof data === "object" && (data.parcelId || data.ParcelId || data.recordSerialNo || data.RecordSerialNo)) {
    return [data];
  }
  return [];
}

/**
 * @param {object} row
 * @returns {import("../types/adminWalkInBooking.types").WalkInUserOption}
 */
export function normalizeWalkInUser(row = {}) {
  const id = pick(row.id, row.Id, row.userId, row.UserId);
  const userName = pick(
    row.userName,
    row.UserName,
    row.username,
    row.Username,
    row.name,
    row.Name,
  );
  return {
    id: id ? String(id) : "",
    userName,
    name: pick(row.name, row.Name, row.fullName, row.FullName, userName),
    email: pick(row.email, row.Email, row.userEmail, row.UserEmail),
    mobile: pick(row.mobile, row.Mobile, row.mobileNo, row.MobileNo, row.phone),
  };
}

/**
 * @param {object} row
 * @returns {import("../types/adminWalkInBooking.types").WalkInCreatedAccount}
 */
function normalizeCreatedAccount(row = {}) {
  return {
    party: pick(row.party, row.Party, row.role, row.Role),
    userName: pick(row.userName, row.UserName, row.username, row.Username),
    password: pick(row.password, row.Password, row.temporaryPassword),
    email: pick(row.email, row.Email),
    isNew: row.isNew ?? row.IsNew ?? true,
  };
}

/**
 * @param {object} data
 * @returns {import("../types/adminWalkInBooking.types").WalkInCreateResult}
 */
export function normalizeWalkInCreateResult(data = {}) {
  const root = data?.data && typeof data.data === "object" ? data.data : data;
  const accountsRaw =
    root.createdAccounts ||
    root.CreatedAccounts ||
    root.newAccounts ||
    root.NewAccounts ||
    [];
  return {
    parcelId:
      root.parcelId ??
      root.ParcelId ??
      root.id ??
      root.Id ??
      null,
    recordSerialNo: pick(
      root.recordSerialNo,
      root.RecordSerialNo,
      root.bookingNo,
      root.number,
    ),
    trackingToken: pickWalkInTrackingToken(root),
    senderTrackingToken: pick(
      root.senderTrackingToken,
      root.SenderTrackingToken,
    ),
    receiverTrackingToken: pick(
      root.receiverTrackingToken,
      root.ReceiverTrackingToken,
    ),
    createdAccounts: (Array.isArray(accountsRaw) ? accountsRaw : []).map(
      normalizeCreatedAccount,
    ),
    paymentInvoiceNo: pick(
      root.paymentInvoiceNo,
      root.PaymentInvoiceNo,
      root.invoiceNumber,
      root.InvoiceNumber,
      root.invoiceNo,
    ),
    paymentStatusName: pick(
      root.paymentStatusName,
      root.PaymentStatusName,
      root.statusName,
      root.StatusName,
    ),
    raw: root,
  };
}

/**
 * @param {object} row
 * @returns {import("../types/adminWalkInBooking.types").WalkInBookingListRow}
 */
export function normalizeWalkInListRow(row = {}) {
  const amountRaw = row.amount ?? row.Amount ?? row.orderPayableAmount ?? row.OrderPayableAmount;
  const amountNum = Number(amountRaw);
  return {
    id: row.id ?? row.Id ?? row.parcelId ?? row.ParcelId,
    parcelId: row.parcelId ?? row.ParcelId ?? row.id ?? row.Id,
    recordSerialNo: pick(row.recordSerialNo, row.RecordSerialNo, row.parcelSerial),
    senderName: pick(row.senderName, row.SenderName),
    receiverName: pick(row.receiverName, row.ReceiverName),
    bookingOwner: pick(row.bookingOwner, row.BookingOwner, row.owner),
    amount: Number.isFinite(amountNum) ? amountNum : 0,
    currency: pick(row.currency, row.Currency, row.symbol, row.Symbol, "GBP"),
    creationDate: pick(
      row.creationDate,
      row.CreationDate,
      row.createdAt,
      row.CreatedAt,
    ),
    trackingToken: pickWalkInTrackingToken(row),
    senderTrackingToken: pick(
      row.senderTrackingToken,
      row.SenderTrackingToken,
    ),
    receiverTrackingToken: pick(
      row.receiverTrackingToken,
      row.ReceiverTrackingToken,
    ),
  };
}

function asList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.details)) return value.details;
  return [];
}

function joinAddress(...parts) {
  return parts.map((p) => String(p || "").trim()).filter(Boolean).join(" · ");
}

function firstNestedRow(obj = {}) {
  const details = asList(obj.details || obj.Details);
  if (details[0]) return details[0];
  const products = asList(obj.products || obj.Products || obj.lines);
  return products[0] || {};
}

function pickRouteName(...sources) {
  for (const src of sources) {
    if (src == null) continue;
    if (typeof src === "string" && src.trim() && src.trim().toLowerCase() !== "unknown route") {
      return src.trim();
    }
    if (typeof src !== "object") continue;
    const name = pick(
      src.routeName,
      src.RouteName,
      src.routingTypeName,
      src.RoutingTypeName,
      src.route,
      src.Route,
    );
    if (name && name.toLowerCase() !== "unknown route") return name;
  }
  return "";
}

function pickRouteId(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const id =
      src.routingTypeId ??
      src.RoutingTypeId ??
      src.routeId ??
      src.RouteId ??
      src.routeID;
    if (id != null && String(id).trim() !== "" && Number(id) > 0) {
      return Number(id);
    }
  }
  return null;
}

async function lookupRouteName(routeId) {
  if (!routeId) return "";
  try {
    const { data } = await axios.get(`${getApiBase()}Route`, {
      headers: getAuthHeaders(),
    });
    const rows = asList(data);
    const row = rows.find(
      (r) => String(r.id ?? r.Id ?? r.routeId) === String(routeId),
    );
    return pick(row?.routeName, row?.RouteName, row?.name, row?.Name);
  } catch {
    return "";
  }
}

function normalizeTrackProduct(row = {}, idx = 0) {
  return {
    itemDescription: pick(
      row.uniqItemDescription,
      row.itemDescription,
      row.description,
      `Item ${idx + 1}`,
    ),
    categoryName: pick(
      row.itemCategoryName,
      row.productGroupName,
      row.categoryName,
    ),
    typeName: pick(row.itemTypeName, row.productTypeName, row.typeName),
    qty: Number(row.productQty ?? row.qty ?? row.quantity ?? 1) || 1,
    length: pick(row.parcelLength, row.itemLength, row.length),
    width: pick(row.parcelWidth, row.itemWidth, row.width),
    height: pick(row.parcelHeight, row.itemHeight, row.height),
    weight: pick(
      row.parcelWeight,
      row.itemWeight,
      row.weight,
      row.weightDescription,
    ),
    value: row.itemValue ?? row.productValue ?? row.subTotal ?? "",
    dimensionName: pick(row.dimensionName, row.preset),
    cargoTypeName: pick(row.cargoTypeName),
  };
}

function normalizeTrackStatus(row = {}, idx = 0) {
  return {
    rank: Number(row.rank ?? row.Rank ?? idx + 1) || idx + 1,
    parcelStatusName: pick(
      row.parcelStatusName,
      row.ParcelStatusName,
      row.statusName,
      row.name,
      row.status,
    ),
    isCurrent: Boolean(row.isCurrent ?? row.IsCurrent ?? row.isCurrentStatus),
    isCompleted: Boolean(row.isCompleted ?? row.IsCompleted ?? row.completed),
  };
}

/**
 * GetParcelStatusRankByParcelId is the route template (all steps), not progress.
 * Only the parcel's stored currentParcelStatusName is real until a scan happens.
 */
function pickStoredParcelStatus(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const name = pick(
      src.currentParcelStatusName,
      src.CurrentParcelStatusName,
      src.currentStatusName,
      src.CurrentStatusName,
    );
    if (name) return name;
  }
  return "";
}

/**
 * @param {object} data
 * @returns {import("../types/adminWalkInBooking.types").WalkInPublicTrack}
 */
export function normalizePublicTrack(data = {}) {
  const root = data?.data && typeof data.data === "object" ? data.data : data;
  const amountRaw =
    root.amount ??
    root.Amount ??
    root.orderPayableAmount ??
    root.OrderPayableAmount ??
    root.subTotal;
  const amountNum = Number(amountRaw);
  const productsRaw = asList(
    root.products ||
      root.Products ||
      root.lines ||
      root.Lines ||
      root.details ||
      root.Details ||
      root.items,
  );
  const statusRaw = asList(
    root.statusTimeline ||
      root.StatusTimeline ||
      root.statuses ||
      root.Statuses ||
      root.statusHistory ||
      root.parcelStatuses,
  );
  const senderAddress = joinAddress(
    pick(root.senderDoorNo, root.SenderDoorNo),
    pick(root.senderStreetName, root.SenderStreetName),
    pick(
      root.senderAdditionalAddressInfo,
      root.SenderAdditionalAddressInfo,
      root.senderAddress,
    ),
    pick(root.senderPostCode, root.SenderPostCode),
    pick(root.senderLandMark, root.SenderLandMark),
  );
  const receiverAddress = joinAddress(
    pick(root.receiverDoorNo, root.ReceiverDoorNo),
    pick(root.receiverStreetName, root.ReceiverStreetName),
    pick(
      root.receiverAdditionalAddressInfo,
      root.ReceiverAdditionalAddressInfo,
      root.receiverAddress,
    ),
    pick(root.receiverPostCode, root.ReceiverPostCode),
    pick(root.receiverLandMark, root.ReceiverLandMark),
  );

  return {
    parcelId: root.parcelId ?? root.ParcelId ?? root.id ?? root.Id ?? null,
    recordSerialNo: pick(
      root.recordSerialNo,
      root.RecordSerialNo,
      root.parcelSerial,
      root.ParcelSerial,
    ),
    parcelSerial: pick(
      root.parcelSerial,
      root.ParcelSerial,
      root.recordSerialNo,
      root.RecordSerialNo,
    ),
    senderName: pick(root.senderName, root.SenderName),
    senderEmail: pick(root.senderEmail, root.SenderEmail),
    senderPhone: pick(
      root.senderPhone,
      root.SenderPhone,
      root.senderMobileNo,
      root.SenderMobileNo,
    ),
    senderAddress,
    senderPostCode: pick(root.senderPostCode, root.SenderPostCode),
    receiverName: pick(root.receiverName, root.ReceiverName),
    receiverEmail: pick(root.receiverEmail, root.ReceiverEmail),
    receiverPhone: pick(
      root.receiverPhone,
      root.ReceiverPhone,
      root.receiverMobileNo,
      root.ReceiverMobileNo,
    ),
    receiverAddress,
    receiverPostCode: pick(root.receiverPostCode, root.ReceiverPostCode),
    amount: Number.isFinite(amountNum) ? amountNum : 0,
    currency: pick(
      root.currency,
      root.Currency,
      root.symbol,
      root.Symbol,
      "GBP",
    ),
    status: pickStoredParcelStatus(root),
    paymentStatusName: pick(
      root.paymentStatusName,
      root.PaymentStatusName,
      root.paymentStatus,
      root.statusName,
    ),
    paymentMethodName: pick(
      root.paymentMethodName,
      root.PaymentMethodName,
    ),
    paymentInvoiceNo: pick(
      root.paymentInvoiceNo,
      root.PaymentInvoiceNo,
      root.invoiceNumber,
    ),
    routingTypeId: pickRouteId(root, firstNestedRow(root)),
    routeName: pickRouteName(root, firstNestedRow(root)),
    cargoTypeName: pick(root.cargoTypeName, root.CargoTypeName),
    weightDescription: pick(root.weightDescription, root.WeightDescription),
    dimensionName: pick(root.dimensionName, root.DimensionName),
    uniqItemDescription: pick(
      root.uniqItemDescription,
      root.UniqItemDescription,
      root.itemDescription,
    ),
    shippingServiceName: pick(
      root.shippingServiceName,
      root.ShippingServiceName,
    ),
    products: productsRaw.map(normalizeTrackProduct),
    statusTimeline: statusRaw.map(normalizeTrackStatus),
  };
}

/**
 * @param {string} q
 * @returns {Promise<import("../types/adminWalkInBooking.types").WalkInUserOption[]>}
 */
export async function searchWalkInUsers(q) {
  const query = String(q || "").trim();
  if (query.length < 2) return [];
  const { data } = await axios.get(`${getApiBase()}AdminBooking/SearchUsers`, {
    params: { q: query },
    headers: getAuthHeaders(),
  });
  return unwrapList(data).map(normalizeWalkInUser).filter((u) => u.id || u.userName);
}

/**
 * @param {string} name
 * @returns {Promise<string>}
 */
export async function suggestWalkInUserName(name) {
  const value = String(name || "").trim();
  if (!value) return "";
  const { data } = await axios.get(
    `${getApiBase()}AdminBooking/SuggestUserName`,
    {
      params: { name: value },
      headers: getAuthHeaders(),
    },
  );
  if (typeof data === "string") return data.trim();
  return pick(
    data?.userName,
    data?.UserName,
    data?.username,
    data?.suggestedUserName,
    data?.data,
  );
}

/**
 * @param {object} payload
 * @returns {Promise<import("../types/adminWalkInBooking.types").WalkInCreateResult>}
 */
export async function createAdminWalkInBooking(payload) {
  const body =
    payload?.booking || payload?.Booking
      ? payload
      : buildAdminWalkInCreateBody(payload, {
          isWalkIn: payload?.isWalkIn ?? true,
          createAccountsFor: payload?.createAccountsFor,
          bookingOwner: payload?.bookingOwner,
          senderAccountMode: payload?.senderAccountMode,
          senderExistingUserId: payload?.senderExistingUserId,
          senderUserName: payload?.senderUserName,
          senderEmail: payload?.senderEmail,
          senderPassword: payload?.senderPassword,
          receiverAccountMode: payload?.receiverAccountMode,
          receiverExistingUserId: payload?.receiverExistingUserId,
          receiverUserName: payload?.receiverUserName,
          receiverEmail: payload?.receiverEmail,
          receiverPassword: payload?.receiverPassword,
          senderAccount: payload?.senderAccount,
          receiverAccount: payload?.receiverAccount,
        });

  const { data } = await axios.post(
    `${getApiBase()}AdminBooking/Create`,
    body,
    { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } },
  );
  return normalizeWalkInCreateResult(data);
}

/**
 * @returns {Promise<import("../types/adminWalkInBooking.types").WalkInBookingListRow[]>}
 */
export async function getAdminWalkInBookings() {
  const { data } = await axios.get(
    `${getApiBase()}AdminBooking/GetAdminBookings`,
    { headers: getAuthHeaders() },
  );
  return unwrapList(data).map(normalizeWalkInListRow);
}

export async function listAdminWalkInDrafts() {
  const { data } = await axios.get(`${getApiBase()}AdminBooking/Drafts`, {
    headers: getAuthHeaders(),
  });
  return unwrapList(data);
}

export async function getAdminWalkInDraft(id) {
  const { data } = await axios.get(
    `${getApiBase()}AdminBooking/Draft/${encodeURIComponent(id)}`,
    { headers: getAuthHeaders() },
  );
  return data;
}

export async function upsertAdminWalkInDraft(payload) {
  const { data } = await axios.post(
    `${getApiBase()}AdminBooking/Draft`,
    payload,
    { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } },
  );
  return data;
}

export async function deleteAdminWalkInDraft(id, clientKey) {
  if (id && /^\d+$/.test(String(id))) {
    await axios.post(
      `${getApiBase()}AdminBooking/DeleteDraft/${encodeURIComponent(id)}`,
      {},
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } },
    );
    return;
  }
  if (clientKey) {
    await axios.post(
      `${getApiBase()}AdminBooking/DeleteDraft`,
      { clientKey },
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } },
    );
  }
}

/**
 * Public — no auth.
 * @param {string} token
 * @returns {Promise<import("../types/adminWalkInBooking.types").WalkInPublicTrack>}
 */
export async function trackAdminBooking(token) {
  const { data } = await axios.get(
    `${getApiBase()}AdminBooking/Track/${encodeURIComponent(token)}`,
    { headers: { accept: "*/*" } },
  );
  const track = normalizePublicTrack(data);
  return enrichPublicTrack(track);
}

async function enrichPublicTrack(track) {
  const parcelId = track?.parcelId;
  if (parcelId == null || parcelId === "") return track;

  try {
    const [
      { fetchParcelBookingByParcelNo, fetchParcelProductsForEdit, fetchParcelCostDetails },
      { getInvoicesByParcelId },
      { pickLatestInvoice, invoiceNumberOf },
    ] = await Promise.all([
      import("./adminEditBookingApi"),
      import("./invoiceManagerApi"),
      import("../utils/bookingHistory"),
    ]);

    const [history, products, cost, statusRes, invoices] = await Promise.all([
      fetchParcelBookingByParcelNo(parcelId).catch(() => null),
      fetchParcelProductsForEdit(parcelId).catch(() => []),
      fetchParcelCostDetails(parcelId).catch(() => ({})),
      axios
        .get(
          `${getApiBase()}ParcelStatus/GetParcelStatusRankByParcelId?parcelId=${parcelId}`,
          { headers: getAuthHeaders() },
        )
        .catch(() => ({ data: [] })),
      getInvoicesByParcelId(parcelId).catch(() => []),
    ]);

    const historyObj = history && typeof history === "object" ? history : {};
    const costObj = cost && typeof cost === "object" ? cost : {};
    const latest = pickLatestInvoice(invoices);
    const statusRows = asList(statusRes?.data).map(normalizeTrackStatus);
    const historyDetail = firstNestedRow(historyObj);
    const productSource =
      Array.isArray(products) && products.length
        ? products
        : asList(historyObj.details);
    const productRows = productSource.map(normalizeTrackProduct);

    const routeId = pickRouteId(
      track,
      historyObj,
      historyDetail,
      costObj,
      productSource[0],
      latest,
    );
    let routeName = pickRouteName(
      track,
      historyObj,
      historyDetail,
      costObj,
      productSource[0],
      latest,
    );
    if (!routeName) {
      routeName = await lookupRouteName(routeId);
    }

    return {
      ...track,
      recordSerialNo: pick(
        track.recordSerialNo,
        historyObj.recordSerialNo,
        costObj.recordSerialNo,
      ),
      parcelSerial: pick(
        track.parcelSerial,
        historyObj.recordSerialNo,
        track.recordSerialNo,
      ),
      senderName: pick(track.senderName, historyObj.senderName),
      senderEmail: pick(track.senderEmail, historyObj.senderEmail),
      senderPhone: pick(
        track.senderPhone,
        historyObj.senderPhone,
        historyObj.senderMobileNo,
      ),
      senderAddress: pick(
        track.senderAddress,
        joinAddress(
          historyObj.senderDoorNo,
          historyObj.senderStreetName,
          historyObj.senderAdditionalAddressInfo,
          historyObj.senderPostCode,
        ),
      ),
      receiverName: pick(track.receiverName, historyObj.receiverName),
      receiverEmail: pick(track.receiverEmail, historyObj.receiverEmail),
      receiverPhone: pick(
        track.receiverPhone,
        historyObj.receiverPhone,
        historyObj.receiverMobileNo,
      ),
      receiverAddress: pick(
        track.receiverAddress,
        joinAddress(
          historyObj.receiverDoorNo,
          historyObj.receiverStreetName,
          historyObj.receiverAdditionalAddressInfo,
          historyObj.receiverPostCode,
        ),
      ),
      amount:
        track.amount ||
        Number(
          latest?.orderAmount ??
            costObj.orderPayableAmount ??
            costObj.subTotal ??
            historyObj.orderPayableAmount ??
            0,
        ) ||
        0,
      currency: pick(
        track.currency,
        latest?.symbol,
        costObj.symbol,
        historyObj.symbol,
        "GBP",
      ),
      status: pickStoredParcelStatus(
        { currentParcelStatusName: track.status },
        historyObj,
        historyDetail,
        costObj,
      ),
      paymentStatusName: pick(
        track.paymentStatusName,
        latest?.statusName,
        historyObj.statusName,
      ),
      paymentMethodName: pick(
        track.paymentMethodName,
        latest?.paymentMethodName,
        historyObj.paymentMethodName,
      ),
      paymentInvoiceNo: pick(
        track.paymentInvoiceNo,
        invoiceNumberOf(latest),
      ),
      routingTypeId: track.routingTypeId || routeId,
      routeName,
      cargoTypeName: pick(track.cargoTypeName, historyObj.cargoTypeName),
      weightDescription: pick(
        track.weightDescription,
        historyObj.weightDescription,
      ),
      dimensionName: pick(track.dimensionName, historyObj.dimensionName),
      uniqItemDescription: pick(
        track.uniqItemDescription,
        historyObj.uniqItemDescription,
      ),
      shippingServiceName: pick(
        track.shippingServiceName,
        historyObj.shippingServiceName,
      ),
      products: track.products?.length ? track.products : productRows,
      statusTimeline: track.statusTimeline?.length
        ? track.statusTimeline
        : statusRows,
    };
  } catch {
    return track;
  }
}
